import { action, atom, select } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { AsyncStateN, Iterable, pipe, Set } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { walletsAtom } from '@root/entities/wallet'
import {
  defaultCurrenciesAtom,
  defaultCurrenciesStateAtom,
  loadDefaultCurrenciesAction,
} from '@root/features/currency/default-currencies'
import {
  trendingPairsAtom,
  trendingPairsStateAtom,
} from '@root/features/trending-pairs'
import { reatomSearchToken } from '@root/features/wallet/search-token'

import type { CurrencyAddress } from '@root/entities/currency'
import type { WalletId } from '@root/entities/wallet'

export type CurrencySet = 'wallet' | 'trending' | 'default'

export type CurrencyWithBalance = {
  currency: CurrencyModel
  balance: BigNumber
  balanceUsd: BigNumber
}

type Arguments = {
  walletId: WalletId
  exclude: Array<CurrencyAddress>
  sets: Array<CurrencySet>
  currencies: Array<CurrencyModel>
}

// TODO: Refactor

export const currencySelectorReatom = (options: Arguments) => {
  const optionsAtom = atom(options, 'optionsAtom')

  const setsAtom = atom((ctx) => ctx.spy(optionsAtom).sets, 'setsAtom')
  const walletIdAtom = atom(
    (ctx) => ctx.spy(optionsAtom).walletId,
    'walletIdAtom',
  )
  const fixedCurrenciesAtom = atom(
    (ctx) => ctx.spy(optionsAtom).currencies,
    'fixedCurrenciesAtom',
  )
  const excludedWalletAtom = atom((ctx) => {
    const exclude = select(ctx, (ctx) => ctx.spy(optionsAtom).exclude)
    return Set.create(exclude)
  }, 'excludedWalletAtom')

  const {
    searchQueryAtom,
    setQueryAction,
    searchTokenAction,
    searchStateAtom,
  } = reatomSearchToken()

  const walletAtom = atom((ctx) => {
    const walletId = ctx.spy(walletIdAtom)
    const wallet = select(ctx, (ctx) =>
      ctx.spy(walletsAtom).find((it) => it.id === walletId),
    )

    return wallet
  }, 'walletAtom')

  const initializeAction = action((ctx) => {
    const sets = ctx.get(optionsAtom).sets

    if (sets.includes('default')) {
      loadDefaultCurrenciesAction(ctx)
    }
  }, 'initializeAction')

  const updateOptionsActions = action((ctx, args: Arguments) => {
    optionsAtom(ctx, args)
  }, 'updateOptionsActions')

  const walletCurrenciesAtom = atom((ctx) => {
    const wallet = ctx.spy(walletAtom)
    if (!wallet) {
      return []
    }
    const walletAssets = ctx.spy(wallet.assets)
    const walletCurrencies = walletAssets.map((it) => it.currency)
    return walletCurrencies
  }, 'walletCurrenciesAtom')

  const currenciesAtom = atom((ctx) => {
    const fixedCurrencies = ctx.spy(fixedCurrenciesAtom)
    const currencies: Array<CurrencyModel> = [...fixedCurrencies]
    const wallet = ctx.spy(walletAtom)
    const sets = ctx.spy(setsAtom)
    const query = ctx.spy(searchQueryAtom)

    const currencyWithBalance = (
      currency: CurrencyModel,
    ): CurrencyWithBalance => {
      if (!wallet) {
        return {
          currency: currency,
          balance: BigNumber(0),
          balanceUsd: BigNumber(0),
        }
      }
      const asset = ctx.spy(wallet.assetsByCurrency)[currency.address]
      return {
        currency: currency,
        balance: asset ? ctx.get(asset.balance) : BigNumber(0),
        balanceUsd: asset ? ctx.get(asset.balanceUsd) : BigNumber(0),
      }
    }

    if (sets.includes('wallet')) {
      const walletCurrencies = ctx.spy(walletCurrenciesAtom)
      currencies.push(...walletCurrencies)
    }
    if (sets.includes('default')) {
      const defaultCurrencies = ctx.spy(defaultCurrenciesAtom)
      currencies.push(...defaultCurrencies)
    }
    if (sets.includes('trending')) {
      const trendingPairs = ctx.spy(trendingPairsAtom)
      const trendingCurrencies = trendingPairs.map((it) => it.quote)

      currencies.push(...trendingCurrencies)
    }

    const exclude = ctx.spy(excludedWalletAtom)

    const foundCurrencies = CurrencyModel.searchCurrencies(query)(currencies)

    const result = pipe(
      foundCurrencies,
      Iterable.filter((it) => !exclude.has(it.address)),
      Iterable.uniqBy((it) => it.address),
      Iterable.map((it) => currencyWithBalance(it)),
      Iterable.toArray,
    )

    return result
  }, 'currenciesAtom')

  const isLoadingAtom = atom((ctx) => {
    const sets = ctx.spy(setsAtom)

    if (sets.includes('default')) {
      const state = ctx.spy(defaultCurrenciesStateAtom)
      if (AsyncStateN.isLoading(state)) {
        return true
      }
    }

    if (sets.includes('trending')) {
      const state = ctx.spy(trendingPairsStateAtom)
      if (AsyncStateN.isLoading(state)) {
        return true
      }
    }
  }, 'isLoadingAtom')

  const searchCurrencyStateAtom = atom((ctx) => {
    const searchState = ctx.spy(searchStateAtom)
    const wallet = ctx.spy(walletAtom)

    return pipe(
      searchState,
      AsyncStateN.map((currency): CurrencyWithBalance => {
        if (!wallet) {
          return {
            currency: currency,
            balance: BigNumber(0),
            balanceUsd: BigNumber(0),
          }
        }
        const asset = ctx.spy(wallet.assetsByCurrency)[currency.address]

        return {
          currency: currency,
          balance: asset ? ctx.get(asset.balance) : BigNumber(0),
          balanceUsd: asset ? ctx.get(asset.balanceUsd) : BigNumber(0),
        }
      }),
    )
  }, 'searchCurrencyStateAtom')

  return {
    currenciesAtom,
    initializeAction,
    searchQueryAtom,
    setQueryAction,
    searchTokenAction,
    searchCurrencyStateAtom,
    isLoadingAtom,
    updateOptionsActions,
  }
}
